import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";

export const checkbox = tv({
  base: cn(
    "uipm:relative uipm:inline-flex uipm:items-center uipm:cursor-pointer",
  ),
  variants: {
    state: {
      default: "",
      error: "",
      disabled: "uipm:cursor-not-allowed uipm:opacity-50",
    },
  },
  defaultVariants: {
    state: "default",
  },
});

export const checkboxInput = tv({
  base: cn(
    "uipm:appearance-none uipm:w-6 uipm:h-6 uipm:rounded-[8px]",
    "uipm:border-2 uipm:bg-transparent",
    "uipm:border-[var(--surface-surface-accent)] uipm:checked:bg-[var(--surface-surface-accent)] uipm:checked:border-[var(--surface-surface-accent)]",
    "uipm:transition-all uipm:duration-200",
    "uipm:focus-visible:outline-none uipm:focus-visible:ring-2 uipm:focus-visible:ring-[var(--color-secondary-500)] uipm:focus-visible:ring-offset-2",
    "uipm:cursor-pointer",
  ),
  variants: {
    state: {
      default: "",
      error:
        "uipm:border-[var(--color-color-danger)] uipm:checked:bg-[var(--color-color-danger)] uipm:checked:border-[var(--color-color-danger)]",
      disabled:
        "uipm:cursor-not-allowed uipm:opacity-50 uipm:border-[var(--color-color-light)] uipm:checked:bg-[var(--color-color-light)]",
    },
  },
  defaultVariants: {
    state: "default",
  },
});

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="uipm:w-4 uipm:h-4 uipm:text-white"
  >
    <path
      d="M13.3333 4L6 11.3333L2.66667 8"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export type CheckboxProps = VariantProps<typeof checkbox> &
  VariantProps<typeof checkboxInput> &
  ComponentProps<"input"> & {
    label?: React.ReactNode;
    containerClassName?: string;
  };

export function Checkbox({
  className,
  containerClassName,
  state,
  disabled,
  label,
  ...props
}: CheckboxProps) {
  const checkboxState = disabled ? "disabled" : state;

  return (
    <label
      className={cn(checkbox({ state: checkboxState }), containerClassName)}
    >
      <div className="uipm:relative uipm:inline-flex">
        <input
          type="checkbox"
          disabled={disabled}
          {...props}
          className={cn(checkboxInput({ state: checkboxState }), className)}
        />
        <div className="uipm:absolute uipm:inset-0 uipm:flex uipm:items-center uipm:justify-center uipm:pointer-events-none">
          <CheckIcon />
        </div>
      </div>
      {label && (
        <span
          className={cn(
            "uipm:ml-2 uipm:text-base uipm:leading-[26px]",
            "uipm:text-[var(--typography-colors-text-body)]",
            state === "error" && "uipm:text-[var(--color-color-danger)]",
          )}
        >
          {label}
        </span>
      )}
    </label>
  );
}
